/**
 * Pure operations over the Workbook model (`office-io.ts`).
 *
 * The grid editor never mutates: every op returns a NEW workbook (structural
 * sharing where possible) so Solid signals update predictably and the ops are
 * trivially unit-testable. Rows/columns are dense arrays that auto-grow when
 * a cell outside the current bounds is written; `normalizeForExport` trims
 * trailing emptiness and refreshes formula cached values so the server-side
 * writers (`office-formats`) always serialize an honest snapshot.
 */
import type { Cell, CellValue, Sheet, Workbook } from "./office-io";
import { computeWorkbook } from "./formula";
import {
  rewriteFormulaForSheetRename,
  rewriteFormulaForStructuralOp,
  type StructuralOp,
} from "./formula-rewrite";

const EMPTY: CellValue = { t: "empty" };

export function emptyCell(): Cell {
  return { value: EMPTY };
}

export function emptySheet(name: string): Sheet {
  return { name, rows: [] };
}

export function emptyWorkbook(): Workbook {
  // oxlint-disable-next-line lingui/no-unlocalized-strings -- default sheet name, matches Excel
  return { sheets: [emptySheet("Sheet 1")] };
}

/**
 * Parses what the user typed into a cell:
 *  · `=...`  → formula (value recomputed by the engine)
 *  · number-looking → number, `true`/`false` → bool
 *  · empty string → empty cell
 *  · anything else → text
 */
export function parseCellInput(raw: string): Cell {
  const trimmed = raw.trim();
  if (trimmed === "") {
    return { value: EMPTY };
  }
  if (trimmed.startsWith("=")) {
    return { value: EMPTY, formula: trimmed.slice(1) };
  }
  const upper = trimmed.toUpperCase();
  if (upper === "TRUE") {
    return { value: { t: "bool", v: true } };
  }
  if (upper === "FALSE") {
    return { value: { t: "bool", v: false } };
  }
  const n = Number(trimmed);
  if (!Number.isNaN(n) && /^[+-]?(\d+\.?\d*|\.\d+)([eE][+-]?\d+)?$/.test(trimmed)) {
    return { value: { t: "number", v: n } };
  }
  return { value: { t: "text", v: raw } };
}

/** The editable raw text of a cell (what the formula bar shows). */
export function cellInputText(cell: Cell | undefined): string {
  if (!cell) {
    return "";
  }
  if (cell.formula) {
    return `=${cell.formula}`;
  }
  const v = cell.value;
  if (v.t === "number") {
    return String(v.v);
  }
  if (v.t === "bool") {
    return v.v ? "TRUE" : "FALSE";
  }
  if (v.t === "text") {
    return v.v;
  }
  return "";
}

function padRow(row: Cell[], cols: number): Cell[] {
  if (row.length >= cols) {
    return row;
  }
  return [...row, ...Array.from({ length: cols - row.length }, emptyCell)];
}

/** Writes `cell` at (row, col), growing the grid as needed. */
export function setCell(sheet: Sheet, row: number, col: number, cell: Cell): Sheet {
  const rows = [...sheet.rows];
  while (rows.length <= row) {
    rows.push([]);
  }
  const newRow = padRow([...(rows[row] ?? [])], col + 1);
  newRow[col] = cell;
  rows[row] = newRow;
  return { ...sheet, rows };
}

export function setSheet(workbook: Workbook, index: number, sheet: Sheet): Workbook {
  const sheets = [...workbook.sheets];
  sheets[index] = sheet;
  return { sheets };
}

export function addSheet(workbook: Workbook): Workbook {
  const base = "Sheet";
  let n = workbook.sheets.length + 1;
  const names = new Set(workbook.sheets.map((s) => s.name));
  while (names.has(`${base} ${n}`)) {
    n++;
  }
  return { sheets: [...workbook.sheets, emptySheet(`${base} ${n}`)] };
}

/** Rewrites every formula of every sheet (identity-preserving when unchanged). */
function rewriteAllFormulas(
  workbook: Workbook,
  rewrite: (formula: string, formulaSheet: string) => string,
): Workbook {
  return {
    sheets: workbook.sheets.map((sheet) => ({
      ...sheet,
      rows: sheet.rows.map((row) =>
        row.map((cell) => {
          if (!cell.formula) {
            return cell;
          }
          const next = rewrite(cell.formula, sheet.name);
          return next === cell.formula ? cell : { ...cell, formula: next };
        }),
      ),
    })),
  };
}

export function renameSheet(workbook: Workbook, index: number, name: string): Workbook {
  const sheet = workbook.sheets[index];
  if (!sheet) {
    return workbook;
  }
  const newName = name.trim() || sheet.name;
  const renamed = setSheet(workbook, index, { ...sheet, name: newName });
  if (newName === sheet.name) {
    return renamed;
  }
  // Cross-sheet formulas referencing the old name must follow the rename.
  return rewriteAllFormulas(renamed, (formula) =>
    rewriteFormulaForSheetRename(formula, sheet.name, newName),
  );
}

export function removeSheet(workbook: Workbook, index: number): Workbook {
  if (workbook.sheets.length <= 1) {
    return workbook;
  } // never drop the last sheet
  return { sheets: workbook.sheets.filter((_, i) => i !== index) };
}

// ── Structural edits (insert/delete rows and columns) ────────────────────────

/**
 * Applies the structural change to the target sheet's dense rows and then
 * rewrites every formula of every sheet so refs keep pointing at the same
 * data (refs into a deleted span become `#REF!`). See `formula-rewrite.ts`.
 */
function structuralEdit(
  workbook: Workbook,
  sheetIndex: number,
  op: StructuralOp,
  editRows: (rows: Cell[][]) => Cell[][],
): Workbook {
  const target = workbook.sheets[sheetIndex];
  if (!target || op.at < 0 || op.count < 1) {
    return workbook;
  }
  const edited = setSheet(workbook, sheetIndex, { ...target, rows: editRows([...target.rows]) });
  return rewriteAllFormulas(edited, (formula, formulaSheet) =>
    rewriteFormulaForStructuralOp(formula, op, target.name, formulaSheet),
  );
}

/** Inserts `count` blank rows so the first lands AT `at` (0-based). */
export function insertRows(
  workbook: Workbook,
  sheetIndex: number,
  at: number,
  count = 1,
): Workbook {
  return structuralEdit(workbook, sheetIndex, { kind: "insertRows", at, count }, (rows) => {
    if (at < rows.length) {
      rows.splice(at, 0, ...Array.from({ length: count }, (): Cell[] => []));
    }
    return rows;
  });
}

/** Deletes `count` rows starting at `at` (refs into them become `#REF!`). */
export function deleteRows(
  workbook: Workbook,
  sheetIndex: number,
  at: number,
  count = 1,
): Workbook {
  return structuralEdit(workbook, sheetIndex, { kind: "deleteRows", at, count }, (rows) => {
    rows.splice(at, count);
    return rows;
  });
}

/** Inserts `count` blank columns so the first lands AT `at` (0-based). */
export function insertCols(
  workbook: Workbook,
  sheetIndex: number,
  at: number,
  count = 1,
): Workbook {
  return structuralEdit(workbook, sheetIndex, { kind: "insertCols", at, count }, (rows) =>
    rows.map((row) =>
      // Rows shorter than `at` have no cell at/after the insertion point.
      row.length > at
        ? [...row.slice(0, at), ...Array.from({ length: count }, emptyCell), ...row.slice(at)]
        : row,
    ),
  );
}

/** Deletes `count` columns starting at `at` (refs into them become `#REF!`). */
export function deleteCols(
  workbook: Workbook,
  sheetIndex: number,
  at: number,
  count = 1,
): Workbook {
  return structuralEdit(workbook, sheetIndex, { kind: "deleteCols", at, count }, (rows) =>
    rows.map((row) => (row.length > at ? [...row.slice(0, at), ...row.slice(at + count)] : row)),
  );
}

/** Data bounds of a sheet: rows/cols that actually hold content. */
export function sheetBounds(sheet: Sheet): { rows: number; cols: number } {
  let rows = 0;
  let cols = 0;
  sheet.rows.forEach((row, r) => {
    row.forEach((cell, c) => {
      if (cell.formula || cell.value.t !== "empty") {
        rows = Math.max(rows, r + 1);
        cols = Math.max(cols, c + 1);
      }
    });
  });
  return { rows, cols };
}

/**
 * Export-ready copy: trailing empty rows/cells trimmed and every formula
 * cell's cached `value` refreshed from the engine (so `.xlsx`/`.csv`/`.ods`
 * writers serialize current results, not stale caches).
 */
export function normalizeForExport(workbook: Workbook): Workbook {
  const computedAll = computeWorkbook(workbook);
  return {
    sheets: workbook.sheets.map((sheet, sheetIndex) => {
      const computed = computedAll[sheetIndex] ?? new Map<string, never>();
      const { rows: rowCount } = sheetBounds(sheet);
      const rows: Cell[][] = [];
      for (let r = 0; r < rowCount; r++) {
        const srcRow = sheet.rows[r] ?? [];
        let lastNonEmpty = -1;
        const outRow: Cell[] = srcRow.map((cell, c) => {
          if (!cell.formula) {
            if (cell.value.t !== "empty") {
              lastNonEmpty = c;
            }
            return cell;
          }
          const result = computed.get(`${r},${c}`);
          lastNonEmpty = c;
          const value: CellValue =
            result && result.value !== null && !result.error
              ? typeof result.value === "number"
                ? { t: "number", v: result.value }
                : typeof result.value === "boolean"
                  ? { t: "bool", v: result.value }
                  : { t: "text", v: result.value }
              : result?.error
                ? { t: "text", v: result.error }
                : EMPTY;
          return { ...cell, value };
        });
        rows.push(outRow.slice(0, lastNonEmpty + 1));
      }
      return { ...sheet, rows };
    }),
  };
}
